/**
 * Thin HTTP client for the public API routes the SPA depends on.
 *
 * Public read routes (used to load the owner's live content on page load):
 *   - `GET /gallery` → `{ images: [...] }` published portfolio images
 *   - `GET /pricing` → `{ pricing }` current investment pricing
 *   - `GET /faq`     → `{ faqs: [...] }` FAQ entries
 *   - `GET /hero`    → `{ renditionKey }` owner-managed hero image rendition
 *
 * Booking routes:
 *   - `GET /availability?from=&to=` → status for every day in a range, used to
 *     paint the calendar (Req 6.1). The response is `{ results: [...] }` where
 *     each entry is exactly `{ date, status }` — no calendar detail (Req 6.2).
 *   - `POST /bookings`              → submit a booking inquiry (Req 8). On a
 *     validation failure the API returns field-level `validationErrors`; on an
 *     unavailable date it returns 409.
 *
 * The base URL is injected at build time via `VITE_API_BASE_URL` (empty string
 * means same-origin / relative). Rendition keys are resolved to displayable
 * URLs via {@link assetUrl} using `VITE_ASSET_BASE_URL`. The public read
 * functions never throw: any network/non-2xx outcome maps to a safe fallback
 * (empty gallery, default pricing/FAQ, null hero) so the page always renders.
 */

import type {
  AvailabilityResult,
  BookingSubmission,
  FaqEntry,
  Image,
  PricingConfig,
} from "@photography-booking/shared";

import { DEFAULT_FAQS, DEFAULT_PRICING } from "../content/site-content.js";

/** Configured API origin; empty string keeps requests same-origin. */
export const API_BASE_URL: string = (
  import.meta.env.VITE_API_BASE_URL ?? ""
).replace(/\/+$/, "");

/** Builds an absolute (or relative) API URL for `path`. */
export function apiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

/**
 * Base URL that image/hero rendition keys are served from (e.g. a CDN in front
 * of the images bucket), injected at build time via `VITE_ASSET_BASE_URL`.
 * Empty string keeps keys relative / same-origin.
 */
export const ASSET_BASE_URL: string = (
  import.meta.env.VITE_ASSET_BASE_URL ?? ""
).replace(/\/+$/, "");

/**
 * Resolves a storage rendition key (as returned by the gallery/hero routes)
 * to a displayable URL.
 *
 * Keys that are already absolute (`http(s)://…`) are returned unchanged. When
 * no {@link ASSET_BASE_URL} is configured the key is returned as-is (relative /
 * same-origin); otherwise it is joined onto the asset base. Used for both the
 * gallery cards and the hero backdrop so URL resolution stays consistent.
 */
export function assetUrl(key: string): string {
  if (/^https?:\/\//i.test(key)) {
    return key;
  }
  if (!ASSET_BASE_URL) {
    return key;
  }
  return `${ASSET_BASE_URL}/${key.replace(/^\/+/, "")}`;
}

/** A single field-level validation error echoed by the bookings API. */
export interface ApiValidationError {
  field: string;
  rule: string;
  message: string;
}

/**
 * True when an API-provided message is too technical to show a non-technical
 * Owner/Visitor (bare HTTP status codes, "Request failed…" transport
 * boilerplate, or unsupported-route strings like "POST is not supported on
 * /gallery"). Such messages are swapped for a plain-language fallback so the
 * user never sees raw status numbers or routing internals.
 *
 * Exported so the authenticated admin client can apply the same rule.
 */
export function isUnfriendlyMessage(message: string | undefined): boolean {
  if (!message || message.trim() === "") {
    return true;
  }
  return (
    /status\s+\d{3}/i.test(message) ||
    /\bis not supported on\b/i.test(message) ||
    /\brequest failed\b/i.test(message) ||
    /^\s*\d{3}\b/.test(message)
  );
}

/** Discriminated result of submitting a booking inquiry. */
export type SubmitBookingResult =
  | {
      ok: true;
      bookingId: string;
      status: string;
      requestedDate: string;
    }
  | {
      ok: false;
      /** Machine-readable error code from the API (or a transport code). */
      code: string;
      /** Human-readable message safe to surface to the Visitor. */
      message: string;
      /** Field-level errors when the failure was a validation rejection. */
      validationErrors?: ApiValidationError[];
    };

/**
 * Fetches availability for an inclusive ISO date range.
 *
 * Returns one {@link AvailabilityResult} per day. Throws on transport failure
 * or a non-2xx response so the caller can show a single "couldn't load" state
 * for the calendar.
 */
export async function fetchAvailabilityRange(
  from: string,
  to: string,
): Promise<AvailabilityResult[]> {
  const query = new URLSearchParams({ from, to }).toString();
  const response = await fetch(apiUrl(`/availability?${query}`), {
    method: "GET",
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`Availability request failed with status ${response.status}`);
  }

  const body = (await response.json()) as { results?: AvailabilityResult[] };
  return body.results ?? [];
}

/**
 * Fetches the published gallery images from `GET /gallery`.
 *
 * Tolerates the `{ images: [...] }` envelope (or a bare array). On any
 * network/non-2xx/parse failure returns an empty list so the Gallery renders
 * its empty-state placeholder instead of breaking the page.
 */
export async function fetchGallery(): Promise<Image[]> {
  try {
    const response = await fetch(apiUrl("/gallery"), {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      return [];
    }
    const body = (await response.json()) as
      | { images?: Image[] }
      | Image[];
    const images = Array.isArray(body) ? body : body.images;
    return images ?? [];
  } catch {
    return [];
  }
}

/**
 * Fetches the current investment pricing from `GET /pricing`.
 *
 * Tolerates the `{ pricing: {...} }` envelope (or a bare object). On any
 * network/non-2xx/parse failure returns {@link DEFAULT_PRICING} so the
 * Investment section always has values to show.
 */
export async function fetchPricing(): Promise<PricingConfig> {
  try {
    const response = await fetch(apiUrl("/pricing"), {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      return DEFAULT_PRICING;
    }
    const body = (await response.json()) as
      | { pricing?: PricingConfig }
      | PricingConfig;
    const pricing =
      "pricing" in body ? (body as { pricing?: PricingConfig }).pricing : body;
    return (pricing as PricingConfig | undefined) ?? DEFAULT_PRICING;
  } catch {
    return DEFAULT_PRICING;
  }
}

/**
 * Fetches the FAQ entries from `GET /faq`.
 *
 * Tolerates the `{ faqs: [...] }` envelope (or a bare array). On any
 * network/non-2xx/parse failure returns {@link DEFAULT_FAQS} so the FAQ
 * section always has content.
 */
export async function fetchFaqs(): Promise<FaqEntry[]> {
  try {
    const response = await fetch(apiUrl("/faq"), {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      return [...DEFAULT_FAQS];
    }
    const body = (await response.json()) as { faqs?: FaqEntry[] } | FaqEntry[];
    const faqs = Array.isArray(body) ? body : body.faqs;
    return faqs && faqs.length > 0 ? faqs : [...DEFAULT_FAQS];
  } catch {
    return [...DEFAULT_FAQS];
  }
}

/**
 * Fetches the current hero (homepage background) image from `GET /hero`.
 *
 * Returns `{ renditionKey }` where the key is the Owner-configured hero
 * rendition, or `null` when no hero is set, its rendition is pending, or any
 * transport/parse failure occurs. The caller falls back to the CSS backdrop on
 * `null`, so the homepage always renders.
 */
export async function fetchHero(): Promise<{
  renditionKey: string | null;
  photoCredit: string | null;
}> {
  try {
    const response = await fetch(apiUrl("/hero"), {
      method: "GET",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) {
      return { renditionKey: null, photoCredit: null };
    }
    const body = (await response.json()) as {
      renditionKey?: string | null;
      photoCredit?: string | null;
    };
    return {
      renditionKey: body.renditionKey ?? null,
      photoCredit: body.photoCredit ?? null,
    };
  } catch {
    return { renditionKey: null, photoCredit: null };
  }
}

/**
 * Submits a booking inquiry to `POST /bookings`.
 *
 * Maps the API's status codes to a typed result:
 *   - 201 → `{ ok: true, ... }`
 *   - 400 → validation failure (carries `validationErrors`)
 *   - 409 → the requested date is unavailable
 *   - anything else / network error → a generic failure
 *
 * Never throws; transport errors are returned as `{ ok: false, ... }`.
 */
export async function submitBooking(
  submission: BookingSubmission,
): Promise<SubmitBookingResult> {
  let response: Response;
  try {
    response = await fetch(apiUrl("/bookings"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(submission),
    });
  } catch {
    return {
      ok: false,
      code: "NETWORK_ERROR",
      message:
        "We couldn't reach the studio just now. Please check your connection and try again.",
    };
  }

  let body: {
    bookingId?: string;
    status?: string;
    requestedDate?: string;
    error?: { code?: string; message?: string };
    validationErrors?: ApiValidationError[];
  } = {};
  try {
    body = await response.json();
  } catch {
    // A 2xx with an unparseable body is still treated as a failure below.
  }

  if (response.ok && body.bookingId) {
    return {
      ok: true,
      bookingId: body.bookingId,
      status: body.status ?? "PENDING",
      requestedDate: body.requestedDate ?? submission.requestedDate ?? "",
    };
  }

  return {
    ok: false,
    code: body.error?.code ?? "REQUEST_FAILED",
    message: friendlyBookingMessage(body),
    validationErrors: body.validationErrors,
  };
}

/**
 * Maps a failed booking response to a plain-language message for the Visitor.
 *
 * A `reference` (present on a server-side failure) is surfaced so the Visitor
 * can quote it if the problem persists. A field/validation message that is
 * already friendly is kept as-is; anything technical (or a server error
 * without a friendly message) becomes a generic, reassuring fallback. The
 * Visitor never sees a raw status code.
 */
function friendlyBookingMessage(body: {
  error?: { message?: string; reference?: string };
}): string {
  const reference = body.error?.reference;
  if (reference) {
    return `Something went wrong on our end. Please try again in a moment. If it keeps happening, note this reference: ${reference}.`;
  }
  const message = body.error?.message;
  if (!isUnfriendlyMessage(message)) {
    return message as string;
  }
  return "Something went wrong on our end. Please try again in a moment.";
}
